import { defineCustomElement } from '~/lit/define-custom-element';
import { html } from 'lit-element';
import { LitEntity } from '../../utility/lit-entity';
import { resolveCurrentBinReading } from '~/domain/bins/resolve-current-bin-reading';
import './bin-smoothing-enabled';

class BinLatestLevel extends LitEntity {
  constructor() {
    super();
    this.latestLevelLoaded = false;
    this.useSmoothedValues = true;
  }

  static get properties() {
    return {
      latestLevelHref: { type: String, attribute: false },
      latestValidLevelHref: { type: String, attribute: false },
      latestLevel: { type: Object, attribute: false },
      latestValidLevel: { type: Object, attribute: false },
      latestLevelLoaded: { type: Boolean, attribute: false },
      latestValidLevelLoaded: { type: Boolean, attribute: false },
      useSmoothedValues: { type: Boolean, attribute: false },
      latestLabel: { type: String, attribute: false },
    };
  }

  render() {
    return html`
      <lit-entity .href="${this.latestLevelHref}" @update="${e => this._latestLevelChanged(e.detail)}"></lit-entity>
      ${this.latestValidLevelHref
    ? html`<lit-entity .href="${this.latestValidLevelHref}" @update="${e => this._latestValidLevelChanged(e.detail)}"></lit-entity>`
    : ''}
      <bin-smoothing-enabled .href="${this.href}" @smoothingEnabled="${e => this._smoothingEnabledChanged(e)}"></bin-smoothing-enabled>
    `;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    const latestLevelHref = this.getHref('https://api.binsentry.com/rel/bin-level-latest');
    const latestValidLevelHref = this.getHref('https://api.binsentry.com/rel/bin-level-latest-valid');

    if (latestLevelHref !== this.latestLevelHref) {
      this.latestLevelLoaded = false;
      this.latestLevelHref = latestLevelHref;
      this.latestLevel = undefined;
    }

    if (latestValidLevelHref !== this.latestValidLevelHref) {
      this.latestValidLevelLoaded = !latestValidLevelHref;
      this.latestValidLevelHref = latestValidLevelHref;
      this.latestValidLevel = undefined;
    }
  }

  update(changed) {
    super.update(changed);
    this._checkLevelUpdate(changed);
  }

  _checkLevelUpdate(changed) {
    // require all to be loaded
    if (
      !this.entity
      || !this.entity.properties
      || !this.latestLevelLoaded
      || !this.latestValidLevelLoaded
      || typeof this.useSmoothedValues === 'undefined'
    ) {
      return;
    }

    // only fire event if something changed
    if (!(changed.has('entity') || changed.has('latestLevel') || changed.has('latestValidLevel') || changed.has('useSmoothedValues'))) {
      return;
    }

    const resolvedReading = resolveCurrentBinReading({
      binEntity: this.entity,
      latestLevelEntity: this.latestLevel,
      latestValidLevelEntity: this.latestValidLevel,
    });
    const levelEntity = resolvedReading.levelEntity;
    let volume;
    if (levelEntity) {
      volume = this.useSmoothedValues
        ? levelEntity.properties.estimatedVolume ?? levelEntity.properties.volume
        : levelEntity.properties.volume;
    }

    this.dispatchEvent(new CustomEvent('levelUpdate', {
      detail: {
        latestLevel: this.latestLevel,
        latestValidLevel: this.latestValidLevel,
        levelEntity,
        volume,
        latestLabel: this.latestLabel,
        source: resolvedReading.source,
        status: resolvedReading.status,
      },
    }));
  }

  _latestLevelChanged({ entity }) {
    this.latestLevelLoaded = true;
    this.latestLevel = entity;
    this.latestLabel = entity?.properties.label;
  }

  _latestValidLevelChanged({ entity }) {
    this.latestValidLevelLoaded = true;
    this.latestValidLevel = entity;
  }

  _smoothingEnabledChanged({ detail }) {
    const { smoothingEnabled } = detail;
    this.useSmoothedValues = smoothingEnabled;
  }
}

defineCustomElement('bin-latest-level', BinLatestLevel);
